<template>
  <div class="notice-form">
    <h2>{{ mode === 'edit' ? '공지사항 수정' : '공지사항 등록' }}</h2>

    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="title">제목</label>
        <input type="text" id="title" v-model="form.title" required />
      </div>

      <div class="form-group">
        <label>내용</label>
        <ToastEditor v-model="form.content" />
      </div>

      <div class="form-group checkbox-group">
        <label>
          <input type="checkbox" v-model="form.topFixed" />
          📌 상단 고정
        </label>
      </div>

            <!-- ───── 파일 업로드 ───── -->
      <div class="form-group">
        <label for="files">파일 업로드</label>
        <input type="file" id="files" @change="handleFileUpload" multiple />
      </div>

      <!-- ───── 첨부 파일 목록 (신규 + 기존) ───── -->
      <div class="form-group" v-if="Object.keys(fileUrl).length">
        <label>첨부된 파일</label>
        <ul>
          <li v-for="(url, name) in fileUrl" :key="name">
            파일 이름: {{ name }}
            <button type="button" @click="removeFile(name)">x</button>
          </li>
        </ul>
      </div>
      
      <button type="submit">{{ mode === 'edit' ? '수정' : '등록' }}</button>
    </form>

    <router-link to="/admin" class="back">← 목록으로</router-link>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import ToastEditor from "@/components/ToastEditor.vue";
import axios from '@/utils/axios';

/* ─────────────────────────
   1. props & 기본 상태
   ───────────────────────── */
const props = defineProps({
  mode:  { type: String, default: 'create' }, // 'create' | 'edit'
  id:    { type: [String, Number], default: null }
});
const router = useRouter();

const form = ref({
  title:     '',
  content:   '',
  topFixed:  false
});

/* ─────────────────────────
   2. 파일 업로드 상태
   ───────────────────────── */
const fileUrl      = ref({});  // {파일명: 미리보기 URL | 기존 파일 URL}
let   selectedFiles = [];      // 새로 선택한 File 객체들
const deletedFiles = ref([]);  // 삭제 요청된 파일명

/* ─────────────────────────
   3. 파일 선택 핸들러
   ───────────────────────── */
const handleFileUpload = (e) => {
  const newFiles = Array.from(e.target.files);
  newFiles.forEach(file => {
    // 중복 방지
    if (!selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
      selectedFiles.push(file);
      fileUrl.value[file.name] = URL.createObjectURL(file);
    }
  });
};

/* ─────────────────────────
   4. 파일 삭제 핸들러
   ───────────────────────── */
const removeFile = (name) => {
  if (fileUrl.value[name]) {
    delete fileUrl.value[name];
    // 기존 파일이면 삭제 목록에 포함
    if (!selectedFiles.find(f => f.name === name)) {
      deletedFiles.value.push(name);
    } else {
      // 새로 올릴 파일을 취소한 경우 selectedFiles에서도 제거
      selectedFiles = selectedFiles.filter(f => f.name !== name);
    }
  }
};

/* ─────────────────────────
   5. 제출
   ───────────────────────── */
const handleSubmit = () => {
  (props.mode === 'edit') ? saveNotice('put') : saveNotice('post');
};

function saveNotice(method) {
  /* FormData 구성 */
  const data = new FormData();
  data.append(
    'notice',
    new Blob([JSON.stringify(form.value)], { type: 'application/json' })
  );
  if (deletedFiles.value.length) {
    data.append('deletedFiles', JSON.stringify(deletedFiles.value));
  }
  selectedFiles.forEach(file => data.append('files', file));

  axios({
    method,
    url: method === 'post'
      ? '/bx201/admin/notice'
      : `/bx201/admin/notice/${props.id}`,
    data
    // 👉 FormData는 Content-Type 자동 설정 (headers 생략)
  })
    .then(() => {
      alert(method === 'post' ? '공지사항 등록 완료!' : '공지사항 수정 완료!');
      router.push('/admin');
    });
}

/* ─────────────────────────
   6. 수정 모드: 기존 데이터 로드
   ───────────────────────── */
onMounted(() => {
  if (props.mode === 'edit' && props.id) {
    axios.get(`/bx201/notice/${props.id}`)
      .then(({ data }) => {
        form.value.title    = data.title;
        form.value.content  = data.content;
        form.value.topFixed = data.topFixed;
        /* 서버에서 내려주는 파일 정보 → {파일명: URL} 형태라고 가정 */
        fileUrl.value       = data.fileUrl || {};
      });
  }
});
</script>

<style scoped>
/* 기존 스타일 유지 + 약간의 여백 */
.notice-form      { max-width: 800px; margin: 80px auto; padding: 0 16px; }
form              { display: flex; flex-direction: column; gap: 20px; }
.form-group       { display: flex; flex-direction: column; }
.form-group label { margin-bottom: 6px; font-weight: bold; }
input[type='text']{ padding: 10px; font-size: 1rem; border: 1px solid #ccc; }
.checkbox-group   { flex-direction: row; align-items: center; gap: 8px; }
button            { padding: 12px; background: #111; color: #fff; font-weight: bold; border: none; cursor: pointer; }
button:hover      { background: #333; }
button[type='button']{ color: red; background: transparent; border: none; cursor: pointer; }
button[type='button']:hover{ text-decoration: underline; }
.back             { display: inline-block; margin-top: 24px; color: #007bff; text-decoration: none; }
</style>
